/**
 * Consent Creation Form Component
 * Reference implementation following UI Standards
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ConsentCreate } from '../../types/consent';

const ConsentForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<ConsentCreate>({
    learner_id: 0,
    consent_type: '',
    granted: true,
    ip_address: '',
    user_agent: '',
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!formData.learner_id || formData.learner_id === 0) errors.learner_id = 'Learner ID is required';
    if (!formData.consent_type || formData.consent_type.trim() === '') errors.consent_type = 'Consent type is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? 0 : Number(value)) : value) }));
    if (validationErrors[name]) {
      setValidationErrors(prev => { const newErrors = { ...prev }; delete newErrors[name]; return newErrors; });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        ip_address: formData.ip_address || null,
        user_agent: formData.user_agent || null,
      };

      await axios.post('/api/consents/', payload);
      setSuccess(true);
      setTimeout(() => navigate('/consents'), 1500);
    } catch (err: any) {
      console.error('Error creating consent:', err);
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail.map((e: any) => `${e.loc.join('.')}: ${e.msg}`).join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to create consent. Please check your input.');
        }
      } else {
        setError('Failed to create consent. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/consents')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>Consents</MuiLink>
        <Typography color="text.primary">New Consent</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Create New Consent</Typography>
        <Typography variant="body2" color="textSecondary">Record learner consent for data processing</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Consent recorded successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth required type="number" label="Learner ID" name="learner_id"
                value={formData.learner_id || ''} onChange={handleChange}
                error={!!validationErrors.learner_id} helperText={validationErrors.learner_id} disabled={loading} />
              <TextField fullWidth required label="Consent Type" name="consent_type"
                value={formData.consent_type} onChange={handleChange}
                error={!!validationErrors.consent_type} helperText={validationErrors.consent_type || 'e.g., data_processing, marketing'}
                disabled={loading} />
            </Stack>

            <FormControlLabel
              control={<Checkbox checked={formData.granted} onChange={handleChange} name="granted" disabled={loading} />}
              label="Consent Granted"
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth label="IP Address" name="ip_address"
                value={formData.ip_address || ''} onChange={handleChange} disabled={loading} helperText="Optional" />
              <TextField fullWidth label="User Agent" name="user_agent"
                value={formData.user_agent || ''} onChange={handleChange} disabled={loading} helperText="Optional" />
            </Stack>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/consents')} disabled={loading}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}>
                {loading ? 'Recording...' : 'Record Consent'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default ConsentForm;
